/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/media-has-caption */
import { errorService } from "app/utils";
import React, { useRef, useState, useEffect } from "react";
import { useReactMediaRecorder } from "react-media-recorder";

declare global {
  interface MediaRecorderOptions {
    mimeType?: string;
    audioBitsPerSecond?: number;
    videoBitsPerSecond?: number;
    bitsPerSecond?: number;
  }

  interface MediaRecorder extends EventTarget {
    readonly state: "inactive" | "recording" | "paused";
    readonly stream: MediaStream;
    readonly mimeType: string;
    readonly videoBitsPerSecond: number;
    readonly audioBitsPerSecond: number;
    ondataavailable: ((this: MediaRecorder, ev: BlobEvent) => any) | null;
    onerror: ((this: MediaRecorder, ev: ErrorEvent) => any) | null;
    onpause: ((this: MediaRecorder, ev: Event) => any) | null;
    onresume: ((this: MediaRecorder, ev: Event) => any) | null;
    onstart: ((this: MediaRecorder, ev: Event) => any) | null;
    onstop: ((this: MediaRecorder, ev: Event) => any) | null;
    start(timeslice?: number): void;
    stop(): void;
    pause(): void;
    resume(): void;
    requestData(): void;
  }

  interface BlobEvent extends Event {
    readonly data: Blob;
    readonly timecode: number;
  }

  interface Window {
    MediaRecorder: {
      prototype: MediaRecorder;
      new(stream: MediaStream, options?: MediaRecorderOptions): MediaRecorder;
    };
  }
}

interface CameraRecorderProps {
  audioConstraints: any;
  videoConstraints: any;
  customMediaStream: any;
  status: "recording" | "stopped" | "paused" | "idle";
  onFinishRecord: (blobUrl: string) => void;
  onClick: () => void;
}

const CameraRecorderEffects: React.FC<CameraRecorderProps> = ({
  customMediaStream, videoConstraints, audioConstraints, status, onFinishRecord, onClick
}) => {
  const [finished, setFinished] = useState(false);
  const videoRef = useRef<any>(null);
  const isMounted = useRef(true);
  const recorder = useReactMediaRecorder({
    video: videoConstraints,
    audio: audioConstraints,
    customMediaStream
  });

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  useEffect(() => {
    if (!isMounted.current) return;

    if (videoRef.current && customMediaStream) {
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = recorder.previewStream;
        if (recorder.status === "idle") {
          recorder.startRecording();
        }
      }
    }
  }, [customMediaStream, recorder]);

  useEffect(() => {
    if (!isMounted.current) return;

    if (recorder.mediaBlobUrl && !finished) {
      // Validate if the mediaBlobUrl is a valid blob URL
      try {
        const url = new URL(recorder.mediaBlobUrl);
        if (url.protocol === "blob:") {
          onFinishRecord(recorder.mediaBlobUrl);
          setFinished(true);
        } else {
          errorService.error("Invalid blob URL received from recorder", {
            mediaBlobUrl: recorder.mediaBlobUrl,
            recorderStatus: recorder.status,
            hasPreviewStream: !!recorder.previewStream
          });
        }
      } catch (error) {
        const err = error as Error;
        errorService.error("Invalid mediaBlobUrl received", {
          error: err.toString(),
          mediaBlobUrl: recorder.mediaBlobUrl,
          recorderStatus: recorder.status,
          hasPreviewStream: !!recorder.previewStream,
          errorName: err.name,
          errorMessage: err.message
        });
        // If we have a previewStream, we can try to create a fallback blob
        if (recorder.previewStream) {
          const mediaRecorder = new window.MediaRecorder(recorder.previewStream);
          const chunks: Blob[] = [];

          mediaRecorder.ondataavailable = e => {
            if (e.data.size > 0) {
              chunks.push(e.data);
            }
          };

          mediaRecorder.onstop = () => {
            const blob = new Blob(chunks, { type: "video/webm" });
            const fallbackBlobUrl = URL.createObjectURL(blob);
            onFinishRecord(fallbackBlobUrl);
            setFinished(true);
          };

          mediaRecorder.start();
          setTimeout(() => mediaRecorder.stop(), 0);
        }
      }
    }
  }, [recorder.mediaBlobUrl]);

  useEffect(() => {
    if (!isMounted.current) return;

    switch (status) {
      case "stopped":
        recorder.stopRecording();
        break;
      case "paused":
        recorder.pauseRecording();
        break;
      case "recording":
        if (recorder.status === "paused") recorder.resumeRecording();
        break;
      default:
        break;
    }
  }, [status]);

  if (status === "stopped") {
    return (<></>);
  }

  return (
    <>
      <video onClick={onClick} className="Video-Preview__Source-Video" ref={videoRef} width={1080} autoPlay />
    </>
  );
};

export default CameraRecorderEffects;
